/**
 * KPI ページのチームスコープ解決ヘルパー。
 *
 * `kpi.tsx` から切り出したもの（PMO ビュー追加でファイルが
 * `cortex-quality/max-code-lines` の 500 行上限を超えたため）。
 * 先頭 `-` の付いたファイルは TanStack Router のルート生成対象外。
 *
 * 表示名はこのファイルで再宣言せず `@cortex/domain-product-innovation-lab-kpi-team-filter` の
 * `TEAM_OPTIONS`（5 画面共通のプルダウンカタログ）を正本とする。ここに残るのは
 * 「カタログに無いが KPI 画面だけで使う値（SRE / PMO）」と親子関係だけ。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain UX
 * @graph-business KPIページのチーム表示名変換と親子チーム展開ロジック
 * @graph-connects none
 */

import { getTeamOptionLabel } from '@cortex/domain-product-innovation-lab-kpi-team-filter';
import type { KpiTeam } from '@cortex/domain-product-innovation-lab-types';

/**
 * 共通カタログに無い、この画面固有の表示名。
 * SRE はプルダウンから外しているが既存データの行表示で出る。PMO はチームではない専用ビュー。
 * @graph-connects none
 */
const EXTRA_DISPLAY_NAMES: Record<string, string> = {
  sre: 'SRE',
  pmo: 'PMO',
};

/**
 * 親チームと子チームの関係。
 *
 * PDG TE の 2 サブチームは `team_name` を持たず nick_name で割る括りのため、
 * **両者の合計は TE 合計と一致しない**（どちらにも属さない TE メンバーがいる）。
 * 親の行を子で置き換えるのではなく、親（tet）自身も残して並べる。
 * @graph-connects none
 */
const TEAM_HIERARCHY: Record<string, KpiTeam[]> = {
  pdg: ['pdg', 'tet', 'growth', 'experience'],
  tet: ['tet', 'growth', 'experience'],
  ace: ['ace', 'aircloset', 'platform', 'mall', 'qa'],
};

/**
 * @graph-stack product-innovation-lab
 * @graph-domain UX
 * @graph-business チーム識別子を日本語表示名（例: pdg→「PDG」）に変換
 * @graph-connects getTeamOptionLabel [calls] 共通カタログの表示名を参照
 */
export function getTeamDisplayName(team: string): string {
  return EXTRA_DISPLAY_NAMES[team] ?? getTeamOptionLabel(team);
}

/**
 * @graph-stack product-innovation-lab
 * @graph-domain UX
 * @graph-business 選択チームから表示対象チーム配列を決定。親チーム選択時は子チームを展開、allはnull（全表示）を返却
 * @graph-connects none
 */
export function getTeamsToShow(selectedTeam: string | undefined): string[] | null {
  if (!selectedTeam || selectedTeam === 'all') {
    return null; // 全て表示（SRE除外はフィルタで処理）
  }
  // 親チームの場合は子チームも含める
  const childTeams = TEAM_HIERARCHY[selectedTeam] as string[] | undefined;
  if (childTeams) {
    return childTeams;
  }
  // 子チームの場合はそのチームのみ
  return [selectedTeam];
}
